<template>
  <div
    class="bottom-panel border-top bg-light pane"
    :style="editPanelStyle"
    :data-test="isOpen? 'bottom-panel-open':'bottom-panel-closed'"
    ref="bottomPanel"
  >
    <div
      v-if="isOpen"
      class="resizer"
      ref="myDiv"
      @mousedown="onMouseDown"
    />
    <b-tabs
      class="edit-panel-tab-nav sidebar-tab-nav h-100"
      nav-class="border-0 px-3 flex-column flex-lg-row bg-light nav-tabs"
      content-class="edit-content-wrapper border-top overflow-auto"
      justified
      v-if="(contentItem)"
      v-model="activeTab"
    >
      <template v-slot:tabs-start>
        <li
          class="nav-item align-self-center"
          data-test="bottom-panel-expand-button"
        >
          <div
            class="nav-link"
            @click="toggleSidebar"
          >
            <i
              :class="['toggle-icon','fal',setToggleIcon,'fa-sm']"
            />
          </div>
        </li>
      </template>
      <b-tab
        @click="showEditPanel()"
        title="Copy"
        button-id="copy-tab"
        data-test="edit-panel-copy-panel"
      >
        <CopyPanel
          class="mb-5"
          v-if="isCreativeSet"
          :content-item="contentItem"
          :read-only="readOnly"
          @updateContentItem="updateContentItem"
          ref="copyPanel"
        />
      </b-tab>
      <b-tab
        @click="showEditPanel()"
        title="Image"
        button-id="image-tab"
        data-test="edit-panel-image-panel"
        lazy
        v-if="canEditCreative"
      >
        <ImagePanel
          :id="contentItem.id"
          :content="contentItem"
          :read-only="readOnly"
          :feed-id="feedId"
          :creative="creative"
          @updateContentItem="updateContentItem"
          @refreshContentList="refreshContentList"
          @deleteContentImage="deleteContentImage"
        />
      </b-tab>
      <b-tab
        @click="showEditPanel()"
        title="Trackers"
        button-id="trackers-tab"
        data-test="edit-panel-trackers-panel"
        :disabled="!isCreativeSet"
        lazy
        v-if="canEditCreative && !isManagedCampaign"
      >
        <TrackerPanel
          v-if="isCreativeSet"
          :read-only="readOnly"
          :encoded-external-id="contentItem.encodedExternalId"
        />
      </b-tab>
      <b-tab
        v-if="isManagedCampaign"
        @click="showEditPanel()"
        title="Custom Properties"
        button-id="custom-properties-tab"
        data-test="custom-properties-tab"
      >
        <CustomPropertiesPanel
          class="mb-5"
          v-if="isCreativeSet"
          :read-only="readOnly"
          @updateContentItem="updateContentItem"
          :encoded-external-id="contentItem.encodedExternalId"
          :advertiser-id="advertisersId"
          :creative="creative"
          ref="customPropertiesPanel"
        />
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import {
  mapActions, mapMutations, mapState, mapGetters,
} from 'vuex';
import * as types from '@/modules/content/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';


import CopyPanel from '@/modules/content/components/CopyPanel';
import ImagePanel from '@/modules/content/components/ImagePanel';
import TrackerPanel from '@/modules/content/components/TrackerPanel';
import CustomPropertiesPanel from '@/modules/content/components/CustomPropertiesPanel';

export default {
  name: 'BottomPanel',
  components: {
    CopyPanel,
    ImagePanel,
    TrackerPanel,
    CustomPropertiesPanel,
  },
  data() {
    return {
      activeTab: null,
      isOpen: false,
      parentHeight: 0,
      panelHeight: 400,
      initialPageY: 0,
      currentCreativeId: 0,
    };
  },
  props: {
    feedId: {
      type: Number,
      default: 0,
    },
    advertisersId: {
      type: Number,
      default: 0,
    },
    readOnly: {
      type: Array,
      default: () => [],
    },
    viewType: {
      type: String,
      default: 'Editor',
    },
    contentItem: {
      type: Object,
      default: () => {},
    },
    creativeSet: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'campaignFeedId',
      'order',
    ]),
    ...mapGetters('campaigns', {
      isCampaignReadonly: types.GET_IS_CAMPAIGN_READONLY,
      isManagedCampaign: campaignTypes.GET_IS_MANAGED_CAMPAIGN,
    }),
    ...mapState('content', [
      'totalPublished',
    ]),
    ...mapGetters('content', {
      creative: types.GET_CURRENT_SELECTED_CREATIVE,
    }),
    panelMarginBottom() {
      const newMargin = this.panelHeight - 55;
      return (newMargin <= 64) ? 64 : newMargin;
    },
    editPanelStyle() {
      return {
        height: `${this.panelHeight}px`,
        marginBottom: this.isOpen ? '' : `-${this.panelMarginBottom}px`,
      };
    },
    isCreativeSet() {
      return this.creative ? !!this.creative.id : false;
    },
    canEditCreative() {
      return this.viewType !== 'Showcase';
    },
    setToggleIcon() {
      return this.isOpen ? 'fa-chevron-down' : 'fa-chevron-up';
    },
    getTabs() {
      const tabs = [];

      if (this.viewType === 'Setup' || this.viewType === 'Editor') {
        tabs.push({
          component: 'CopyPanel',
          name: 'Copy',
        },
        {
          component: 'ImagePanel',
          name: 'Image',
        },
        {
          component: 'CustomProperties',
          name: 'Custom Properties',
        });
      }

      if ((this.viewType === 'Setup' || this.viewType !== 'Editor') && this.isCampaignReadonly) {
        tabs.push({
          component: 'TrackerPanel',
          name: 'Trackers',
        });
      }
      return tabs;
    },
  },
  methods: {
    ...mapActions('content', [
      types.PATCH_CREATIVE,
      types.REMOVE_CURRENT_ANNOTATION,
      types.UPDATE_CONTENT_DISPLAY_COPY,
    ]),
    ...mapMutations('content', [
      types.TOGGLE_ANNOTATIONS,
      types.SET_CONTENT_TOTAL_PUBLISHED_ITEMS,
      types.SET_CREATIVE_BOTTOM_PANEL_IS_OPEN,
    ]),

    updateContentItem(item) {
      let newPublishedCount = this.totalPublished;
      if (item.contentItem.state === 'PUBLISHED') {
        newPublishedCount = this.totalPublished - 1;
      }
      const contentPayload = {
        creativeId: this.currentCreativeId,
        advertisersId: this.advertisersId,
        feedId: this.feedId,
        id: item.contentItem.encodedExternalId,
        body: {
          ...item.body,
        },
      };
      this.UPDATE_CONTENT_DISPLAY_COPY(contentPayload)
        .then(() => {
          this.refreshContentList();
          this.SET_CONTENT_TOTAL_PUBLISHED_ITEMS(newPublishedCount);
        });
    },
    isActive(activeTab) {
      return (this.activeTab === activeTab);
    },
    toggleSidebar() {
      this.isOpen ? this.hideSidebar() : this.showEditPanel();
    },
    showEditPanel() {
      this.currentCreativeId = this.creativeSet.id;
      document.addEventListener('mousedown', this.documentClick);
      this.isOpen = true;
      this.SET_CREATIVE_BOTTOM_PANEL_IS_OPEN(true);
    },
    hideSidebar() {
      document.removeEventListener('mousedown', this.documentClick);
      this.setTimestamp();
      this.isOpen = false;
      this.SET_CREATIVE_BOTTOM_PANEL_IS_OPEN(false);
    },
    documentClick(e) {
      const el = this.$refs.bottomPanel;
      if (!el) return;
      const { target } = e;
      const modalEl = document.getElementsByClassName('modal')[0];
      if (modalEl) {
        if (modalEl) {
          if (target !== modalEl && !modalEl.contains(target)) {
            return false;
          }
        }
      } else if (el !== target && !el.contains(target)) {
        this.hideSidebar();
      }
    },
    deleteContentImage() {
      this.contentItem.imageUrl = '/images/image-not-set.png';
      this.refreshContentList();
    },
    refreshContentList() {
      this.$emit('refreshContentList');
      this.setTimestamp();
    },
    setTimestamp() {
      this.$emit('contentLoad');
    },
    onMouseMove(target) {
      const { movementY } = target;
      const currentHeight = this.panelHeight;
      this.panelHeight = (currentHeight + -movementY);
    },
    onMouseUp() {
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
    },
    onMouseDown({ pageY }) {
      this.initialPageY = pageY;
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    },
  },
  mounted() {
    this.parentHeight = this.$parent.$el.offsetHeight;
    this.panelHeight = 450;
  },
};
</script>

<style lang="scss" scoped>
.bottom-panel{
  margin-bottom: 0;
  position: absolute;
  right: 0px;
  left: 0;
  bottom: 0px;
  transition: margin .5s;
  min-height: 120px;
  & .nav-tabs .nav-item {
    i {
      margin: 0;
      width: 2rem;
      text-align: center;
    }
  }
}
.edit-panel-tabs{
  max-width: 100%;
}

.edit-panel .nav {
  justify-content: space-around;
}

.nav-link{
  width: 100%;
  text-align: center;
  display: block;
}
.edit-content-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100% - 53px)!important;
}
.tabs-wrapper{
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}
#creative-wrapper-background{
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
}
.resizer{
  position: relative;
  height: 20px;
  width: 100%;
  cursor: row-resize;
  background: #fff;
  border-bottom: 1px solid rgba(0, 40, 100, 0.12);

  &:before {
    display: block;
    content: "";
    width: 3px;
    height: 39px;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-top: -20px;
    margin-left: -1.5px;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
    transform: rotate(90deg);
  }
}
.bottom-panel-padding {
  padding: 1.5rem 5.5rem 1.5rem 1.5rem !important;
}
</style>
